import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, UserPlus, Trash2, Loader2, Users, Shield, User, Eye, EyeOff, Wrench, Settings2, Key } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "user", nombre: "", apellido: "" });
  const [creating, setCreating] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});  // Para mostrar/ocultar contraseñas
  const [showNewPassword, setShowNewPassword] = useState(false);  // Para el formulario de crear
  const [editingPassword, setEditingPassword] = useState(null);  // ID del usuario editando contraseña
  const [newPassword, setNewPassword] = useState("");  // Nueva contraseña
  const [editingRole, setEditingRole] = useState(null);  // ID del usuario editando rol

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, { headers });
      setUsers(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (userId, username) => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    
    try {
      await axios.put(`${API}/auth/users/${userId}/password`, 
        { new_password: newPassword },
        { headers }
      );
      toast.success(`Contraseña de ${username} actualizada`);
      setEditingPassword(null);
      setNewPassword("");
      fetchUsers();
    } catch (error) {
      toast.error("Error al actualizar contraseña");
    }
  };

  const handleUpdateRole = async (userId, username, newRole) => {
    try {
      await axios.put(`${API}/auth/users/${userId}/role`, 
        { new_role: newRole },
        { headers }
      );
      toast.success(`Rol de ${username} cambiado a ${newRole}`);
      setEditingRole(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar rol");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error("Completa todos los campos");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/auth/users`, newUser, { headers });
      toast.success(`Usuario ${newUser.username} creado`);
      setNewUser({ username: "", password: "", role: "user", nombre: "", apellido: "" });
      setCreateOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`¿Eliminar usuario ${username}?`)) return;

    try {
      await axios.delete(`${API}/auth/users/${userId}`, { headers });
      toast.success(`Usuario ${username} eliminado`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-zinc-100">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-heading font-black text-xl uppercase tracking-widest text-zinc-100">
                Gestión de Usuarios
              </h1>
              <p className="text-zinc-500 text-sm">Administrar accesos al sistema</p>
            </div>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                <UserPlus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100">
                  Crear Usuario
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                      Nombre
                    </Label>
                    <Input
                      value={newUser.nombre}
                      onChange={(e) => setNewUser(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Juan"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                      Apellido
                    </Label>
                    <Input
                      value={newUser.apellido}
                      onChange={(e) => setNewUser(prev => ({ ...prev, apellido: e.target.value }))}
                      placeholder="García"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Nombre de Usuario
                  </Label>
                  <Input
                    value={newUser.username}
                    onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="usuario"
                    className="bg-zinc-800 border-zinc-700 text-zinc-100"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Contraseña
                  </Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                    Rol
                  </Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="user">Usuario (Solo lectura)</SelectItem>
                      <SelectItem value="visor">Visor de Sondas (Solo sondas)</SelectItem>
                      <SelectItem value="operator">Operador (Actualiza niveles)</SelectItem>
                      <SelectItem value="supervisor">Supervisor (Configura sensores)</SelectItem>
                      <SelectItem value="admin">Administrador (Control total)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-500">
                    Visor: solo sondas · Usuario: ve todo · Operador: actualiza niveles · Supervisor: configura · Admin: todo
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-zinc-400">
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateUser}
                  disabled={creating}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Crear
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <Card key={user.id} className="bg-[#141415] border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        user.role === "admin" ? "bg-amber-500/20" : 
                        user.role === "supervisor" ? "bg-purple-500/20" :
                        user.role === "operator" ? "bg-emerald-500/20" :
                        "bg-blue-500/20"
                      }`}>
                        {user.role === "admin" ? (
                          <Shield className="w-5 h-5 text-amber-400" />
                        ) : user.role === "supervisor" ? (
                          <Settings2 className="w-5 h-5 text-purple-400" />
                        ) : user.role === "operator" ? (
                          <Wrench className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <User className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-zinc-100">
                          {user.nombre_completo || user.username}
                          {user.nombre_completo && user.nombre_completo !== user.username && (
                            <span className="text-zinc-500 text-xs ml-2">@{user.username}</span>
                          )}
                        </p>
                        {/* Selector de rol o texto */}
                        {editingRole === user.id ? (
                          <Select 
                            defaultValue={user.role}
                            onValueChange={(newRole) => handleUpdateRole(user.id, user.username, newRole)}
                          >
                            <SelectTrigger className="h-6 w-32 bg-zinc-800 border-zinc-700 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="admin" className="text-amber-400">Administrador</SelectItem>
                              <SelectItem value="supervisor" className="text-purple-400">Supervisor</SelectItem>
                              <SelectItem value="operator" className="text-emerald-400">Operador</SelectItem>
                              <SelectItem value="user" className="text-blue-400">Usuario</SelectItem>
                              <SelectItem value="visor" className="text-cyan-400">Visor de Sondas</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <button
                            onClick={() => user.id !== currentUser?.id && setEditingRole(user.id)}
                            className={`text-xs uppercase ${user.id === currentUser?.id ? 'text-zinc-500 cursor-default' : 'text-zinc-500 hover:text-zinc-300 cursor-pointer'}`}
                            disabled={user.id === currentUser?.id}
                          >
                            {user.role === "admin" ? "Administrador" : 
                             user.role === "supervisor" ? "Supervisor" :
                             user.role === "operator" ? "Operador" :
                             user.role === "visor" ? "Visor de Sondas" : "Usuario"}
                            {user.id !== currentUser?.id && <span className="ml-1 text-blue-400">✎</span>}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Contraseña y acciones */}
                    <div className="flex items-center gap-2">
                      {/* Mostrar contraseña si existe */}
                      {user.plain_password ? (
                        <div className="flex items-center gap-2 bg-zinc-800 px-3 py-1.5 rounded">
                          <Key className="w-4 h-4 text-amber-400" />
                          <span className={`font-mono text-sm ${showPasswords[user.id] ? 'text-amber-300' : 'text-zinc-500'}`}>
                            {showPasswords[user.id] ? user.plain_password : '••••••••'}
                          </span>
                          <button
                            onClick={() => setShowPasswords(prev => ({ ...prev, [user.id]: !prev[user.id] }))}
                            className="text-zinc-400 hover:text-zinc-200"
                          >
                            {showPasswords[user.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">Sin contraseña</span>
                      )}
                      
                      {/* Botón cambiar contraseña */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingPassword(editingPassword === user.id ? null : user.id);
                          setNewPassword("");
                        }}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 text-xs"
                      >
                        <Key className="w-3 h-3 mr-1" />
                        Cambiar
                      </Button>
                      
                      {/* Botón eliminar */}
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Formulario cambiar contraseña */}
                  {editingPassword === user.id && (
                    <div className="mt-3 pt-3 border-t border-zinc-700 flex items-center gap-2">
                      <Input
                        type="text"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nueva contraseña..."
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 flex-1 h-8 text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdatePassword(user.id, user.username)}
                        className="bg-amber-600 hover:bg-amber-700 h-8"
                      >
                        Guardar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingPassword(null);
                          setNewPassword("");
                        }}
                        className="h-8"
                      >
                        Cancelar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
